import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

/** Ce qu'il faut pour présenter un dossier dans une tâche (À faire, pop-up). */
export interface CaseSummary {
  id: string;
  company_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  center: string | null;
}

const SUMMARY_SELECT = "id,company_name,client_first_name,client_last_name,center";
/** Au-delà, un résumé en cache est relu (nom ou centre modifié pendant la session). */
const CACHE_TTL_MS = 5 * 60_000;
const cache = new Map<string, { summary: CaseSummary; fetchedAt: number }>();

export function caseSummaryTitle(summary: CaseSummary | null | undefined): string {
  if (!summary) return "Dossier";
  const client = [summary.client_first_name, summary.client_last_name].filter(Boolean).join(" ");
  return summary.company_name || client || "Dossier sans nom";
}

export function caseSummaryClient(summary: CaseSummary | null | undefined): string {
  if (!summary) return "";
  return [summary.client_first_name, summary.client_last_name].filter(Boolean).join(" ");
}

/**
 * Résumés de dossiers par id. `known` (ex. dossiers du Kanban déjà chargés) évite tout appel ;
 * les autres sont lus puis gardés en mémoire quelques minutes (CACHE_TTL_MS).
 * `freshSince` (horodatage) : les résumés lus avant cette date sont relus une fois
 * (ex. ouverture d'une modale), l'ancienne valeur restant affichée en attendant.
 */
export function useCaseSummaries(
  caseIds: readonly string[],
  known?: readonly CaseSummary[],
  options: { freshSince?: number } = {},
): Map<string, CaseSummary> {
  const [version, setVersion] = useState(0);
  const knownById = useMemo(() => new Map((known ?? []).map((c) => [c.id, c])), [known]);
  const key = [...new Set(caseIds)].sort().join(",");
  const { freshSince = 0 } = options;

  useEffect(() => {
    const minFetchedAt = Math.max(freshSince, Date.now() - CACHE_TTL_MS);
    const missing = key
      ? key.split(",").filter((id) => {
          if (knownById.has(id)) return false;
          const cached = cache.get(id);
          return !cached || cached.fetchedAt < minFetchedAt;
        })
      : [];
    if (missing.length === 0) return;
    let alive = true;
    const fetchedAt = Date.now();
    void supabase
      .from("cases")
      .select(SUMMARY_SELECT)
      .in("id", missing)
      .then(({ data, error }) => {
        if (error) {
          console.error("[CaseSummaries] Lecture des dossiers impossible :", error.message);
          return;
        }
        for (const row of (data ?? []) as CaseSummary[]) cache.set(row.id, { summary: row, fetchedAt });
        if (alive) setVersion((v) => v + 1);
      });
    return () => {
      alive = false;
    };
  }, [key, knownById, freshSince]);

  return useMemo(() => {
    const map = new Map<string, CaseSummary>();
    for (const id of key ? key.split(",") : []) {
      const summary = knownById.get(id) ?? cache.get(id)?.summary;
      if (summary) map.set(id, summary);
    }
    return map;
    // `version` force le recalcul quand le cache se remplit.
  }, [key, knownById, version]);
}
